import React from 'react';
import { useHistory } from 'react-router-dom';
import Event from '../../../../../models/eventModel';
import { useTranslation } from 'react-i18next';
import { WeezHeader, Icon, SelectButton, Dropdown, Tooltip, Numbers, ToastContext, Toggle } from '@weezevent/nacre';
import Form from '../../../../../models/form';
import Style from './ticketOfficeEventRates.module.css';
import RatesManager, { generateClassNameRateName } from '../../../../rates/components/rateManager';
import StyleRM from '../../../../rates/components/rateManager.module.css';
import { InheritedProperty } from '../../../../rates/ratesList';
import { Card as NacreCard } from '../../../../../components/card';
import FormModal from './formModal';
import { fetcher } from '@weezevent/weezjs-core';
import { compute_sum_float } from '../../../../../pages/rates/utils';
import LoadingCard from '../../../../../components/loadingCard';

const TicketOfficeEventRates = ({ organization, ticket_office, eventId, syncTicketOffice }) => {
    const [event, loadingEvent] = Event.useApiModel(
        {
            id: eventId,
            organizationId: organization.id,
            cache: false
        },
        [organization, eventId]
    );

    const [forms, loadingForms] = Form.useApiModel(
        {
            organizationId: organization.id,
            eventId: eventId
        },
        [organization, eventId]
    );

    const ticketOfficeEvents = React.useMemo(
        () =>
            ticket_office.events.find(event => {
                return Number(event.id) === Number(eventId);
            }),
        [eventId, ticket_office]
    );

    const findRate = React.useCallback(
        rate => {
            return ticketOfficeEvents.rates.find(r => {
                return Number(r.id) === Number(rate.id);
            });
        },
        [ticketOfficeEvents]
    );

    const findForm = React.useCallback(
        rate => {
            let no_form = {
                name: t('ticket.ticket_office_rates.no_form'),
                id: -1
            };
            let formId = findRate(rate).id_form;
            return formId === -1
                ? no_form
                : forms.find(f => {
                      return Number(f.weez_id) === Number(formId);
                  });
        },
        [forms, findRate]
    );

    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const [selected, setSelected] = React.useState([]);
    const [editFormModal, setEditFormModal] = React.useState(false);

    const multiple_action = React.useCallback(
        type => {
            let rates_id = [];
            selected.forEach(_select => {
                let ticketOfficeRate = findRate(_select);
                rates_id.push(ticketOfficeRate.id);
            });
            let data = {
                rates_id: rates_id
            };
            fetcher
                .post(`/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/event/${event.id}/rate/${type}_visibility_multiple`, { data: data })
                .then(() => {
                    toast.success(t('ticket.ticket_office_rates.toast-success'));
                    syncTicketOffice({
                        allow_cache: false,
                        cache: false
                    });
                })
                .catch(() => {
                    toast.error(t('ticket.ticket_office_rates.toast-error'));
                });
        },
        [organization, ticket_office, event, selected, syncTicketOffice, t, toast]
    );

    const changeVisibility = React.useCallback(
        rate => {
            let ticketOfficeRate = findRate(rate);

            // If we do not have rate in ticketList -> create
            if (!ticketOfficeRate.ticket_office_rate_id) {
                let data = {
                    active: 0,
                    rate_id: ticketOfficeRate.id
                };
                fetcher.post(`/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/event/${eventId}/rate`, { data }).then(() =>
                    syncTicketOffice({
                        allow_cache: false,
                        cache: false
                    })
                );
                return;
            }

            // we already have rate -> update
            let action_name = ticketOfficeRate.active ? 'disable_visibility_multiple' : 'enable_visibility_multiple';
            let data = {
                rates_id: [ticketOfficeRate.id]
            };
            fetcher.post(`/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/event/${eventId}/rate/${action_name}`, { data }).then(() =>
                syncTicketOffice({
                    allow_cache: false,
                    cache: false
                })
            );
        },
        [findRate, organization, ticket_office, eventId]
    );

    const tableCells = [
        {
            header: t('ticket.common.action.select-all'),
            renderRate: (rate, { groups, categories }) => {
                return (
                    <InheritedProperty
                        property="name"
                        object={rate}
                        handleProperty={name => (
                            <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                {name}
                            </div>
                        )}
                        template="rate_template"
                    />
                );
            },
            renderGroup: (group, onToggle) => {
                return (
                    <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                        {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                        <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} className={Style['folder_open_close']} />
                        <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                    </div>
                );
            }
        },
        {
            header: t('ticket.rates.price'),
            renderRate: (rate, { event }) => {
                let _price = rate.rate_template?.price || rate.price;
                const computedPrice = rate.fees?.length && rate.fees.reduce((sum, { amount }) => compute_sum_float(sum, parseFloat(amount) || 0), parseFloat(_price || 0));
                return (
                    <InheritedProperty
                        property="price"
                        object={rate}
                        handleProperty={price => (price !== '0.00' ? <Numbers value={computedPrice || price} currency={event.currency} showNullDecimal /> : t('ticket.rates.free'))}
                        template="rate_template"
                    />
                );
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.rates.visibility'),
            disabledOnClick: true,
            renderRate: (rate, { event }) => {
                if (rate.distributor) {
                    return (
                        <Tooltip triggerOnHover={true} title={t(`ticket.rates.webservicesVisibility`)}>
                            <Icon className={Style['disabled-icon']} name="eye-slash" size="smal" />
                        </Tooltip>
                    );
                }
                return <Toggle checked={findRate(rate).active} onChange={() => changeVisibility(rate)} />;
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.ticket_office_rates.formulaires'),
            renderRate: (rate, { event }) => {
                let form = findForm(rate);
                return (
                    <>
                        <span>{form ? form.name : t('ticket.ticket_office_rates.default_form')}</span>
                        <Icon disabled={rate.distributor} name="edit" onClick={() => setEditFormModal(rate)} />
                    </>
                );
            },
            renderGroup: () => {
                return null;
            }
        }
    ];

    if (loadingForms || loadingEvent || !event) {
        return null;
    }
    return (
        <>
            <WeezHeader
                backLink={{
                    onClick: () => history.push(`/ticket/O${organization.id}/ticket_offices/${ticket_office.id}/events/`)
                }}
                title={ticketOfficeEvents.name}
            />
            <NacreCard separator={false}>
                <LoadingCard loading={loadingForms || loadingEvent}>
                    <RatesManager
                        event={event}
                        organization={organization}
                        initialQuery={{
                            organizationId: organization.id,
                            eventId: eventId
                        }}
                        queryParams={{ expand: 'form' }}
                        tableCells={tableCells}
                        mode={'ticket_office'}
                        onRatesSelected={setSelected}
                        selected={selected}
                        hideEmptyGroup
                    >
                        <div className={Style['details__container-actions']}>
                            <SelectButton
                                tooltipTitle={t('ticket.rates.noActionTooltip')}
                                title={selected.length > 0 ? t('ticket.rates.rateAction', { count: selected.length }) : t('ticket.common.action.bulk')}
                                objects={selected}
                            >
                                <Dropdown.Item
                                    disabled={selected.length === 0}
                                    onClick={() => {
                                        multiple_action('enable');
                                    }}
                                >
                                    {t('ticket.ticket_office_rates.actions.show_rates', { number: selected.length })}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    disabled={selected.length === 0}
                                    onClick={() => {
                                        multiple_action('disable');
                                    }}
                                >
                                    {t('ticket.ticket_office_rates.actions.hide_rates', { number: selected.length })}
                                </Dropdown.Item>
                            </SelectButton>
                        </div>
                    </RatesManager>
                    {editFormModal && (
                        <FormModal
                            editFormModal={editFormModal}
                            ticketOfficeRate={findRate(editFormModal)}
                            setEditFormModal={setEditFormModal}
                            forms={forms}
                            ticket_office={ticket_office}
                            eventId={eventId}
                            organization={organization}
                            syncTicketOffice={syncTicketOffice}
                        />
                    )}
                </LoadingCard>
            </NacreCard>
        </>
    );
};

export default TicketOfficeEventRates;
